import { motion } from 'framer-motion';
import {
  ArrowRight,
  BookOpen,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  Lightbulb,
  Tag,
  Target,
  TrendingUp,
  User,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';

import MarketingPageFooter from '../components/marketing/MarketingPageFooter';
import MarketingLandingNav from '../components/marketing/MarketingLandingNav';
import MarketingAgentBar, { MARKETING_AGENT_STICKY_ZONE_ID } from '../components/marketing/MarketingAgentBar';
import { supabase } from '../lib/supabase';
import { SEO } from '../shared/components/SEO';
import type { BlogPost } from '../types/blog';
import { resolveBlogImageSrc } from '../config/blogImagePaths';

const PAGE_SIZE = 12;

function navigateToBlogPost(
  e: React.MouseEvent<HTMLAnchorElement>,
  slug: string,
  onNavigate?: (view: string) => void,
) {
  if (!onNavigate) return;
  e.preventDefault();
  window.history.pushState({}, '', `/blog/${slug}`);
  onNavigate('blog-post');
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

interface BlogPageProps {
  onNavigate?: (view: string) => void;
}

const BlogPage: React.FC<BlogPageProps> = ({ onNavigate }) => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  // Newsletter subscribe state
  const [subEmail, setSubEmail] = useState('');
  const [subState, setSubState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [subMessage, setSubMessage] = useState<string>('');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = subEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setSubState('error');
      setSubMessage('Please enter a valid email address.');
      return;
    }
    setSubState('loading');
    setSubMessage('');
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('blog-subscribe', {
        body: { email, source: 'blog_page' },
      });
      if (fnErr) throw fnErr;
      if ((data as { error?: string })?.error) throw new Error((data as { error: string }).error);
      setSubState('success');
      setSubMessage(
        (data as { alreadySubscribed?: boolean })?.alreadySubscribed
          ? "You're already subscribed — thanks for sticking around!"
          : 'Subscribed! Check your inbox for a welcome email.',
      );
      setSubEmail('');
    } catch (err) {
      console.error('[BlogPage] subscribe error:', err);
      setSubState('error');
      setSubMessage(
        err instanceof Error ? err.message : 'Could not subscribe. Please try again.',
      );
    }
  };

  // Page derived from URL so back/forward work via AppStateManager popstate
  const [page, setPage] = useState(() => {
    const p = new URLSearchParams(window.location.search).get('page');
    return Math.max(1, parseInt(p ?? '1', 10));
  });

  const offset = (page - 1) * PAGE_SIZE;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: qErr, count } = await supabase
          .from('blog_posts')
          .select('*', { count: 'exact' })
          .eq('status', 'published')
          .order('published_at', { ascending: false })
          .range(offset, offset + PAGE_SIZE - 1);

        if (qErr) throw qErr;
        setPosts((data ?? []) as BlogPost[]);
        setTotal(count ?? 0);
      } catch (err) {
        console.error('[BlogPage] fetch error:', err);
        setError('Failed to load posts. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [page]);

  const goToPage = (n: number) => {
    setPage(n);
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(n));
    window.history.pushState({}, '', url.toString());
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const featuredPost = posts[0] ?? null;
  const gridPosts = posts.slice(1);

  // Derive unique categories from loaded posts for the category strip
  const categories = Array.from(new Set(posts.map((p) => p.category).filter(Boolean))) as string[];

  const categoryIcons: Record<string, React.ElementType> = {
    default: Lightbulb,
    career: TrendingUp,
    industry: Target,
    success: Lightbulb,
  };
  const getCategoryIcon = (cat: string) => {
    const key = cat.toLowerCase().split(' ')[0];
    return categoryIcons[key] ?? categoryIcons.default;
  };

  return (
    <div className="blog-marketing-page relative min-h-screen flex flex-1 flex-col w-full">
      <SEO
        title="Resume & ATS Career Guides (India) | GoGrabJob Blog"
        description="Guides on job search strategy, ATS resume tailoring, cover letters, and interview prep for Indian and global candidates."
        canonical="https://www.gograbjob.com/blog"
      />

      <MarketingLandingNav onNavigate={onNavigate} activeView="blog" variant="marketing" />

      <div
        id={MARKETING_AGENT_STICKY_ZONE_ID}
        className="landing-agent-sticky-zone marketing-agent-sticky-zone--below-nav"
      >
        <MarketingAgentBar onNavigate={onNavigate} />

      <div className="blog-marketing-top">
        <section className="blog-marketing-hero" id="blog-hero">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
            <header className="home-problem-section__hero">
              <p className="home-problem-section__eyebrow">GoGrabJob Blog</p>
              <h1 className="home-problem-section__title text-balance">
                Resume &amp; career
                <span className="home-problem-section__title-accent"> guides</span>
              </h1>
              <p className="home-problem-section__subtitle">
                Practical guides on resume optimisation, ATS tactics, interview prep, and job-search strategy for candidates worldwide.
              </p>
            </header>
          </div>
        </section>

        {!loading && featuredPost && (
          <div className="blog-marketing-top__featured max-w-5xl mx-auto w-full">
            <motion.a
              href={`/blog/${featuredPost.slug}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="blog-card blog-card--featured"
              onClick={(e) => navigateToBlogPost(e, featuredPost.slug, onNavigate)}
            >
              <div className="flex flex-col md:flex-row md:h-[260px]">
                <div className="blog-card__media">
                  {resolveBlogImageSrc(featuredPost.featured_image, featuredPost.slug) && (
                    <img
                      key={featuredPost.featured_image ?? featuredPost.slug}
                      src={resolveBlogImageSrc(featuredPost.featured_image, featuredPost.slug)!}
                      alt={featuredPost.title}
                    />
                  )}
                </div>
                <div className="blog-card__body">
                  <div>
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      <span className="blog-card__badge">Featured</span>
                      {featuredPost.category && (
                        <span className="blog-card__badge blog-card__badge--muted">
                          {featuredPost.category}
                        </span>
                      )}
                    </div>
                    <h2 className="blog-card__title blog-card__title--lg line-clamp-2">{featuredPost.title}</h2>
                    <p className="blog-card__excerpt line-clamp-2">
                      {featuredPost.meta_description ?? featuredPost.hero_intro ?? ''}
                    </p>
                  </div>
                  <div className="flex items-center justify-between flex-wrap gap-2 mt-4">
                    <div className="blog-card__meta">
                      {featuredPost.author_name && (
                        <div className="flex items-center gap-1">
                          <User className="w-3.5 h-3.5" />
                          {featuredPost.author_name}
                        </div>
                      )}
                      {featuredPost.published_at && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {new Date(featuredPost.published_at).toLocaleDateString('en-IN', {
                            day: 'numeric', month: 'short', year: 'numeric',
                          })}
                        </div>
                      )}
                      {featuredPost.read_time_min && (
                        <div className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {featuredPost.read_time_min} min
                        </div>
                      )}
                    </div>
                    <span className="blog-card__link">
                      Read More
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </div>
            </motion.a>
          </div>
        )}
      </div>

      <div className="blog-marketing-main flex-1 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full min-w-0 pb-[calc(2rem+env(safe-area-inset-bottom,0px))]">
        <div className="space-y-12">


          {/* ── Loading ───────────────────────────────────────────────────── */}
          {loading && (
            <div className="flex justify-center py-20">
              <div className="w-10 h-10 rounded-full border-2 border-[var(--vscode-primary)]/20 border-t-[var(--vscode-primary)] animate-spin" />
            </div>
          )}

          {/* ── Error ─────────────────────────────────────────────────────── */}
          {!loading && error && (
            <div className="max-w-xl mx-auto text-center py-12">
              <p className="text-red-400 mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="text-[var(--vscode-primary)] underline hover:opacity-80"
              >
                Try again
              </button>
            </div>
          )}

          {/* ── Empty state ───────────────────────────────────────────────── */}
          {!loading && !error && posts.length === 0 && (
            <div className="max-w-xl mx-auto text-center py-20">
              <BookOpen className="w-12 h-12 mx-auto mb-4 text-[var(--blog-card-muted)]" />
              <p className="text-[var(--blog-card-text)] text-lg">No posts published yet. Check back soon!</p>
            </div>
          )}

          {/* ── Grid ─────────────────────────────────────────────────────── */}
          {!loading && gridPosts.length > 0 && (
            <div className="max-w-6xl mx-auto">
              <h2 className="blog-section-title">Latest Articles</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {gridPosts.map((post, index) => (
                  <motion.a
                    key={post.id}
                    href={`/blog/${post.slug}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: Math.min(index * 0.05, 0.3) }}
                    className="blog-card group"
                    onClick={(e) => navigateToBlogPost(e, post.slug, onNavigate)}
                  >
                    <div className="blog-card__media">
                      {resolveBlogImageSrc(post.featured_image, post.slug) && (
                        <img
                          key={post.featured_image ?? post.slug}
                          src={resolveBlogImageSrc(post.featured_image, post.slug)!}
                          alt={post.title}
                        />
                      )}
                      {post.category && (
                        <div className="absolute top-4 left-4">
                          <span className="blog-card__badge blog-card__badge--muted backdrop-blur-sm">
                            {post.category}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="blog-card__body">
                      <div>
                        <h3 className="blog-card__title blog-card__title--md mb-3 group-hover:text-[var(--blog-link)] transition-colors line-clamp-2">
                          {post.title}
                        </h3>
                        <p className="blog-card__excerpt mb-4 line-clamp-3">
                          {post.meta_description ?? post.hero_intro ?? ''}
                        </p>
                      </div>
                      <div>
                        <div className="blog-card__meta justify-between">
                          <div className="flex items-center gap-3">
                            {post.author_name && (
                              <div className="flex items-center">
                                <User className="w-3 h-3 mr-1" />
                                {post.author_name}
                              </div>
                            )}
                            {post.published_at && (
                              <div className="flex items-center">
                                <Calendar className="w-3 h-3 mr-1" />
                                {new Date(post.published_at).toLocaleDateString('en-IN', {
                                  day: 'numeric', month: 'short', year: 'numeric',
                                })}
                              </div>
                            )}
                          </div>
                          {post.read_time_min && (
                            <div className="flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {post.read_time_min} min
                            </div>
                          )}
                        </div>
                        <div className="mt-4">
                          <span className="blog-card__link">
                            Read Article
                            <ArrowRight className="w-4 h-4 ml-1" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.a>
                ))}
              </div>
            </div>
          )}

          {/* ── Pagination ────────────────────────────────────────────────── */}
          {!loading && totalPages > 1 && (
            <div className="flex justify-center items-center gap-4">
              <button
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
                className="blog-pagination-btn touch-manipulation"
              >
                <ChevronLeft className="w-4 h-4" /> Previous
              </button>
              <span className="blog-pagination-label">Page {page} of {totalPages}</span>
              <button
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
                className="blog-pagination-btn touch-manipulation"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* ── Categories (from real data) ───────────────────────────────── */}
          {!loading && categories.length > 0 && (
            <div className="max-w-4xl mx-auto">
              <h2 className="blog-section-title text-center">Explore by Category</h2>
              <div className="flex flex-wrap justify-center gap-4">
                {categories.map((cat, index) => {
                  const Icon = getCategoryIcon(cat);
                  return (
                    <motion.a
                      key={cat}
                      href={`/blog?category=${encodeURIComponent(cat)}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.05 }}
                      className="blog-category-pill group"
                    >
                      <Tag className="w-4 h-4 text-[var(--blog-link)]" />
                      <Icon className="w-4 h-4 text-[var(--blog-link)]" />
                      <span>{cat}</span>
                    </motion.a>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ── Newsletter — mirrors landing page CTA: illustration left, form right ── */}
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="blog-newsletter flex flex-col md:flex-row w-full md:h-[260px] lg:h-[300px]"
        >
          <div className="blog-newsletter__art">
            <img
              src="/images/blog-illustration.png"
              alt="People collaborating at laptops"
              draggable={false}
            />
          </div>

          <div className="blog-newsletter__form">
            <div className="w-full max-w-sm md:max-w-[22rem] lg:max-w-[24rem] md:ml-auto flex flex-col md:items-end md:text-right">
              <h3 className="blog-newsletter__title">Stay Updated</h3>
              <p className="blog-newsletter__desc">
                Get the latest career insights and job search tips delivered to your inbox.
              </p>
              <form
                onSubmit={handleSubscribe}
                className="flex flex-col sm:flex-row md:flex-col gap-2 w-full"
                noValidate
              >
                <input
                  type="email"
                  required
                  value={subEmail}
                  onChange={(e) => {
                    setSubEmail(e.target.value);
                    if (subState !== 'idle') setSubState('idle');
                  }}
                  placeholder="Enter your email"
                  disabled={subState === 'loading' || subState === 'success'}
                  className="blog-newsletter__input disabled:opacity-60"
                />
                <button
                  type="submit"
                  disabled={subState === 'loading' || subState === 'success'}
                  className="blog-newsletter__btn disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {subState === 'loading' ? 'Subscribing\u2026' : subState === 'success' ? '\u2713 Subscribed' : 'Subscribe'}
                </button>
              </form>
              {subMessage && (
                <p role="status" className={`blog-newsletter__status ${subState === 'error' ? 'blog-newsletter__status--error' : ''}`}>
                  {subMessage}
                </p>
              )}
              <p className="blog-newsletter__fine">
                We&apos;ll only email you about new blog posts. Unsubscribe any time.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
      </div>

      <MarketingPageFooter onNavigate={onNavigate} theme="blog" />
    </div>
  );
};

export default BlogPage;
